import { useEffect, useRef, useState } from 'react';
import '../components/analytics/AnalyticsGuide.css';

/**
 * TipTransactionsGuide — drawer laterale con guida all'uso della pagina Transazioni Mancia.
 */
export default function TipTransactionsGuide() {
    const [open, setOpen] = useState(false);
    const drawerRef = useRef(null);

    useEffect(() => {
        function onKey(e) { if (e.key === 'Escape') setOpen(false); }
        if (open) document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    useEffect(() => {
        function onOutside(e) {
            if (drawerRef.current && !drawerRef.current.contains(e.target)) setOpen(false);
        }
        if (open) document.addEventListener('mousedown', onOutside);
        return () => document.removeEventListener('mousedown', onOutside);
    }, [open]);

    return (
        <>
            <button
                className="metric-drawer-trigger"
                onClick={() => setOpen(true)}
                aria-expanded={open}
            >
                <span className="metric-drawer-trigger-icon">📖</span>
                Guida pagina
            </button>

            {open && <div className="metric-drawer-overlay" aria-hidden="true" />}

            <aside
                ref={drawerRef}
                className={`metric-drawer ${open ? 'metric-drawer--open' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label="Guida pagina Transazioni Mancia"
            >
                <div className="metric-drawer-header">
                    <h2 className="metric-drawer-heading">📖 Guida — Transazioni Mancia</h2>
                    <button className="metric-drawer-close" onClick={() => setOpen(false)} aria-label="Chiudi">✕</button>
                </div>

                <div className="metric-drawer-body">

                    {/* SEZIONE: Come funziona */}
                    <div className="metric-drawer-item">
                        <div className="metric-drawer-item-label">Come usare la pagina</div>
                        <div className="metric-drawer-item-definition">
                            La pagina mostra tutte le transazioni mancia elaborate da Soontip. Usa i filtri per
                            restringere la ricerca per periodo, tenant, venue e stato. Clicca su una riga per
                            aprire il dettaglio completo. Il campo Cerca supporta ID transazione completo e Stripe Payment Intent ID.
                            I KPI in cima si aggiornano in base ai filtri attivi.
                        </div>
                    </div>

                    {/* SEZIONE: Preset date */}
                    <div className="metric-drawer-item">
                        <div className="metric-drawer-item-label">Preset date</div>
                        <div className="metric-drawer-item-definition">
                            La barra dei preset imposta il periodo in un click. <strong>Tutti</strong> rimuove
                            ogni filtro data (attenzione: può restituire molti dati). <strong>Custom</strong>
                            permette di impostare manualmente Da / A. Il pulsante attivo è evidenziato in blu.
                        </div>
                    </div>

                    {/* SEZIONE: KPI cards */}
                    <div className="metric-drawer-item">
                        <div className="metric-drawer-item-label">KPI — Schede riepilogative</div>
                        <div className="metric-drawer-item-definition">
                            Le schede mostrano i contatori del periodo filtrato. Le card marcate come
                            “pag. corrente” si basano sulle righe caricate nella pagina corrente.
                        </div>
                        <table className="tip-guide-table">
                            <tbody>
                                <tr><td>✅ Riuscite</td><td>Conta le transazioni Stripe <code>succeeded</code>; cliccando applica filtro business <code>COLLECTED</code> (incassate con transfer).</td></tr>
                                <tr><td>❌ Non incassate</td><td>Conta gli stati pagamento non incassati (esclusi <code>succeeded</code>, <code>pending</code>, <code>processing</code>)</td></tr>
                                <tr><td>⏳ In attesa</td><td>Conta <code>pending</code> e <code>processing</code></td></tr>
                                <tr><td>🔀 Transfer mancanti</td><td>Stripe <code>succeeded</code> ma nessun transfer registrato (pag. corrente)</td></tr>
                                <tr><td>🧾 Problemi fatturazione</td><td>Non fatturata o errore fattura (pag. corrente)</td></tr>
                                <tr><td>🔎 Anomalie operative</td><td>Note operative terminale o dati da approfondire (pag. corrente)</td></tr>
                            </tbody>
                        </table>
                        <div className="metric-drawer-item-caveat" style={{ marginTop: 8 }}>
                            <span>⚠</span>
                            <span>Transfer mancanti, Problemi fatturazione e Anomalie operative si basano sulla pagina corrente, non su tutte le transazioni.</span>
                        </div>
                    </div>

                    {/* SEZIONE: Filtri rapidi */}
                    <div className="metric-drawer-item">
                        <div className="metric-drawer-item-label">Filtri rapidi</div>
                        <div className="metric-drawer-item-definition">
                            Sotto i filtri principali trovi bottoni applicati <em>lato client</em> sulla pagina caricata:
                        </div>
                        <table className="tip-guide-table">
                            <tbody>
                                <tr><td>❗ Problemi pagamento</td><td>Mostra “Non incassato”, “Transfer mancante” e stati da verificare, escludendo i casi cliente timeout/annullo</td></tr>
                                <tr><td>🧾 Problemi fatturazione</td><td>Mostra “Non fatturata” o “Errore fattura”</td></tr>
                                <tr><td>🙅 Cliente non completa</td><td>Mostra solo i casi <code>COLLECT_TIMEOUT</code> e <code>COLLECT_CANCELED</code></td></tr>
                                <tr><td>🔎 Anomalie operative</td><td>Mostra note operative terminale o stati da approfondire</td></tr>
                            </tbody>
                        </table>
                    </div>

                    {/* SEZIONE: Colonne tabella */}
                    <div className="metric-drawer-item">
                        <div className="metric-drawer-item-label">Colonne della tabella</div>
                        <table className="tip-guide-table">
                            <tbody>
                                <tr><td><strong>ID</strong></td><td>Codice breve TX-XXXXXX. Clicca per copiare l'ID completo.</td></tr>
                                <tr><td><strong>Data</strong></td><td>Giorno/ora della transazione. La bandierina indica il paese della carta.</td></tr>
                                <tr><td><strong>Venue / Tenant</strong></td><td>Locale e azienda owner del terminale.</td></tr>
                                <tr><td><strong>Importo</strong></td><td>Valore della mancia pagata dal cliente.</td></tr>
                                <tr><td><strong>Fee</strong></td><td>Totale fee trattenuta (SoonTip + Stripe). Espande in due righe di dettaglio.</td></tr>
                                <tr><td><strong>Stato pagamento</strong></td><td>Verità economica del pagamento: Incassato, Non incassato, In attesa, Transfer mancante.</td></tr>
                                <tr><td><strong>Transfer</strong></td><td>Stato distribuzione: N transfer, Mancante o —.</td></tr>
                                <tr><td><strong>Fatturazione</strong></td><td>Stato amministrativo separato: Fatturata, Non fatturata, Errore fattura, —.</td></tr>
                                <tr><td><strong>Note</strong></td><td>Indicazioni operative business-friendly con testo e indicatore visivo (badge <code>!</code> / <code>i</code>).</td></tr>
                            </tbody>
                        </table>
                    </div>

                    {/* SEZIONE: Badge Stato pagamento e Note */}
                    <div className="metric-drawer-item">
                        <div className="metric-drawer-item-label">Badge Stato pagamento e Note — significato</div>
                        <table className="tip-guide-table">
                            <tbody>
                                <tr>
                                    <td><span className="tip-guide-badge" style={{ background: '#28a745' }}>Incassato</span></td>
                                    <td>Pagamento economico confermato da Stripe.</td>
                                </tr>
                                <tr>
                                    <td><span className="tip-guide-badge" style={{ background: '#dc3545' }}>Non incassato</span></td>
                                    <td>Stripe non è in stato definitivo positivo (esclusi <code>succeeded</code>, <code>pending</code>, <code>processing</code>).</td>
                                </tr>
                                <tr>
                                    <td><span className="tip-guide-badge" style={{ background: '#17a2b8' }}>In attesa</span></td>
                                    <td>Pagamento non ancora definitivo (<code>pending</code>/<code>processing</code>).</td>
                                </tr>
                                <tr>
                                    <td><span className="tip-guide-badge" style={{ background: '#fd7e14' }}>Transfer mancante</span></td>
                                    <td>Pagamento riuscito ma nessun transfer generato verso i lavoratori.</td>
                                </tr>
                                <tr>
                                    <td><strong>Terminale da verificare</strong></td>
                                    <td>Nota operativa in testo (non badge): outcome terminale anomalo/non coerente, senza cambiare lo stato pagamento.</td>
                                </tr>
                                <tr>
                                    <td><strong>Cliente non completa</strong></td>
                                    <td>Casi <code>COLLECT_TIMEOUT</code> o <code>COLLECT_CANCELED</code>: pagamento non incassato per mancata presentazione carta o annullo cliente.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* SEZIONE: Badge Fatturazione */}
                    <div className="metric-drawer-item">
                        <div className="metric-drawer-item-label">Badge Fatturazione — significato</div>
                        <table className="tip-guide-table">
                            <tbody>
                                <tr><td>✅ Fatturata</td><td>Tutti i transfer hanno una fattura associata.</td></tr>
                                <tr><td>⏳ Non fatturata</td><td>Transfer presenti ma non ancora fatturati o parzialmente fatturati.</td></tr>
                                <tr><td>⚠️ Errore fattura</td><td>Una o più fatture A-Cube in stato <code>error</code> o <code>rejected</code>.</td></tr>
                                <tr><td>—</td><td>Non applicabile (es. pagamento non incassato o in attesa).</td></tr>
                            </tbody>
                        </table>
                    </div>

                    {/* SEZIONE: Esporta */}
                    <div className="metric-drawer-item">
                        <div className="metric-drawer-item-label">Export Excel</div>
                        <div className="metric-drawer-item-definition">
                            Il tasto <strong>⬇ Excel</strong> esporta fino a 500 transazioni con i filtri attivi
                            in formato CSV compatibile Excel (BOM UTF-8, separatore <code>;</code>). Le colonne
                            includono sia etichette business (Stato pagamento, Transfer, Fatturazione, Note)
                            sia campi tecnici (Stripe status, terminal outcome) utili al debug.
                        </div>
                        <div className="metric-drawer-item-caveat" style={{ marginTop: 6 }}>
                            <span>⚠</span>
                            <span>Limite di 500 righe per export. Per dataset più grandi contatta il team tecnico.</span>
                        </div>
                    </div>

                </div>
            </aside>
        </>
    );
}
